import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { X, Send, Sparkles, MessageCircle, User, Phone } from "lucide-react";
import { useAppDispatch } from "../../../../Redux/Hooks";
import { sendWhatsAppMessage } from "../../../../Redux/ApiCalls/Dashboard/WhatsAppAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { countryCodes } from "../../../../Utils/countryCodes";

interface NewConversationModalProps {
  onClose: () => void;
  onSuccess: (recipient: string) => void;
}

const NewConversationModal: React.FC<NewConversationModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [text, setText] = useState("");
  const [custName, setCustName] = useState("Customer");
  const [submitting, setSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 15) {
      setPhone(value);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) {
      showErrorToast("Recipient phone number is required");
      return;
    }
    const cleanCountry = countryCode.replace(/[^\d]/g, "");
    const cleanPhone = phone.replace(/[^\d]/g, "");
    const cleanNum = cleanCountry + cleanPhone;

    if (!text.trim()) {
      showErrorToast("Message text is required");
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        to: cleanNum,
        text: text,
        message_type: "template",
        customer_name: custName,
        template_category: "marketing",
      };

      const res = await dispatch(sendWhatsAppMessage(payload)).unwrap();
      if (res.response.ok) {
        showSuccessToast("WhatsApp message sent successfully!");
        onSuccess(cleanNum);
        onClose();
      } else {
        showErrorToast("Failed to send WhatsApp message");
      }
    } catch (err: any) {
      showErrorToast(err.message || "Failed to start conversation");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCountry =
    countryCodes.find((c) => c.code === countryCode) ?? countryCodes[0];

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Hero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon>
              <MessageCircle size={22} />
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                WhatsApp
              </HeroBadge>
              <Title>New conversation</Title>
              <Subtitle>
                Start a chat by sending a template message to a customer.
              </Subtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </Hero>

        <form onSubmit={handleSend}>
          <Body>
            <Field>
              <Label>
                <Phone size={12} />
                Recipient number
              </Label>
              <PhoneInputWrap ref={dropdownRef}>
                <CountryTrigger
                  type="button"
                  onClick={() => setIsDropdownOpen((open) => !open)}
                >
                  <span>{selectedCountry?.flag}</span>
                  <CountryCodeText>{countryCode}</CountryCodeText>
                  <Chevron $open={isDropdownOpen}>▼</Chevron>
                </CountryTrigger>
                <PhoneDivider />
                <PhoneInput
                  id="contactNo"
                  type="number"
                  placeholder="e.g. 9875082248"
                  value={phone}
                  onChange={handlePhoneChange}
                  required
                />
                {isDropdownOpen && (
                  <DropdownContainer onClick={(e) => e.stopPropagation()}>
                    <SearchInputWrapper>
                      <SearchInput
                        type="text"
                        placeholder="Search country..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </SearchInputWrapper>
                    <CountryList>
                      {countryCodes
                        .filter(
                          (c) =>
                            c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            c.code.includes(searchQuery)
                        )
                        .map((c) => (
                          <CountryListItem
                            key={`${c.name}-${c.code}`}
                            $active={countryCode === c.code}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCountryCode(c.code);
                              setIsDropdownOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <CountryFlag>{c.flag}</CountryFlag>
                            <CountryName>{c.name}</CountryName>
                            <CountryDialCode>{c.code}</CountryDialCode>
                          </CountryListItem>
                        ))}
                    </CountryList>
                  </DropdownContainer>
                )}
              </PhoneInputWrap>
            </Field>

            <Field>
              <Label>
                <User size={12} />
                Customer name
              </Label>
              <Input
                type="text"
                placeholder="e.g. Shalini"
                value={custName}
                onChange={(e) => setCustName(e.target.value)}
              />
              <Hint>Used as template body_1</Hint>
            </Field>

            <Field>
              <Label>
                <MessageCircle size={12} />
                Template body
              </Label>
              <TextArea
                placeholder="Enter the custom body variable..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                required
              />
              <Hint>Used as template body_2</Hint>
            </Field>
          </Body>

          <ActionRow>
            <CancelBtn type="button" onClick={onClose}>
              Cancel
            </CancelBtn>
            <SubmitBtn type="submit" disabled={submitting}>
              <Send size={16} />
              {submitting ? "Sending..." : "Send WhatsApp"}
            </SubmitBtn>
          </ActionRow>
        </form>
      </ModalBox>
    </Overlay>
  );
};

export default NewConversationModal;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  padding: 16px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 92vh;
  overflow: visible;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  border-radius: 22px 22px 0 0;
  flex-shrink: 0;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

const Body = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: min(60vh, 480px);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Hint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.4);
`;

const Input = styled.input`
  height: 44px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  padding: 0 14px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  outline: none;
  background: #fafafa;
  transition: all 0.15s ease;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const TextArea = styled.textarea`
  min-height: 96px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  padding: 12px 14px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  outline: none;
  background: #fafafa;
  resize: vertical;
  transition: all 0.15s ease;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const PhoneInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 12px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  transition: all 0.15s ease;
  &:focus-within {
    background: #fff;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
`;

const CountryTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
`;

const CountryCodeText = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`;

const Chevron = styled.span<{ $open?: boolean }>`
  font-size: 10px;
  color: rgba(23, 23, 23, 0.45);
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  transition: transform 0.15s ease;
`;

const PhoneDivider = styled.div`
  width: 1px;
  height: 18px;
  background: rgba(23, 23, 23, 0.12);
  margin: 0 10px;
  flex-shrink: 0;
`;

const PhoneInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type="number"] {
    -moz-appearance: textfield;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: min(100%, 300px);
  max-height: 240px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(23, 23, 23, 0.12);
  z-index: 20;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchInputWrapper = styled.div`
  padding: 10px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
`;

const SearchInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 10px;
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  font-weight: 600;
  outline: none;
  box-sizing: border-box;
  background: #fafafa;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    background: #fff;
  }
`;

const CountryList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const CountryListItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background-color: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.08)" : "transparent")};
  &:hover {
    background-color: rgba(201, 77, 170, 0.05);
  }
`;

const CountryFlag = styled.span`
  font-size: 16px;
  margin-right: 8px;
`;

const CountryName = styled.span`
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  font-weight: 600;
  color: #171717;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CountryDialCode = styled.span`
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  font-weight: 700;
  color: #c94daa;
  margin-left: 8px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 24px 22px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  flex: 0 0 auto;
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const SubmitBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }
`;
